import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
import { useTranslation } from 'react-i18next';
import { toastError } from '../../features/clu/utils/notify';

/**
 * Centralized Organization Selector Component
 * 
 * Displays a dropdown to switch between user's organizations.
 * Always visible if user has at least one organization.
 * 
 * Features:
 * - Shows current active organization
 * - Dropdown with all available organizations
 * - Role badges for each organization
 * - Handles organization switching with proper state updates
 */
const OrganizationSelector: React.FC = () => {
  const { t } = useTranslation('common');
  const {
    userOrganizations,
    activeOrganization,
    setActiveOrganization,
    loading
  } = useAuth();
  const { isSuperUser } = useEffectiveRole();
  
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        buttonRef.current && 
        !dropdownRef.current.contains(event.target as Node) && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Log for debugging - track all state changes
  useEffect(() => {
    console.log(`[OrganizationSelector] State update:`, {
      loading,
      userOrganizationsCount: userOrganizations?.length || 0,
      userOrganizations: userOrganizations?.map((o: any) => ({ 
        id: o.organization_id, 
        name: o.organization_name, 
        role: o.role_code 
      })) || [],
      activeOrganizationId: activeOrganization?.organization_id,
      activeOrganizationName: activeOrganization?.organization_name,
    });
  }, [loading, userOrganizations, activeOrganization]);

  // Always show selector if we have an activeOrganization (most common case)
  // Also show if we have organizations loaded (even if no activeOrganization yet)
  // Only hide if we're still loading AND have no activeOrganization AND no organizations
  const hasActiveOrg = activeOrganization && activeOrganization.organization_name;
  const hasOrganizations = userOrganizations && userOrganizations.length > 0;
  const shouldShow = hasActiveOrg || hasOrganizations;
  
  if (!shouldShow) {
    // Removed excessive logging that was causing performance issues
    return null;
  }
  
  // Removed excessive logging that was causing performance issues

  const handleOrganizationChange = async (org: any) => {
    if (!org || !org.organization_id) return;

    const orgId = Number(org.organization_id);
    if (isNaN(orgId) || orgId <= 0) return;

    if (activeOrganization?.organization_id === orgId) {
      setShowDropdown(false);
      return;
    }

    // Block suspended orgs for non-super_user (super_user can access all)
    if (!isSuperUser && org.active === false) {
      toastError('Organization suspended', `${org.organization_name || 'This organization'} is suspended and cannot be accessed.`);
      setShowDropdown(false);
      return;
    }

    try {
      await setActiveOrganization(org);
      setShowDropdown(false);
      window.location.reload();
    } catch (error) {
      console.error('[OrganizationSelector] Error changing organization:', error);
      toastError('Error', 'Could not switch organization. Please reload the page.');
    }
  };

  const getCurrentOrganizationName = (): string => {
    if (activeOrganization?.organization_name) {
      return activeOrganization.organization_name;
    }
    
    if (userOrganizations && userOrganizations.length > 0) {
      return userOrganizations[0].organization_name || t('org_selector_no_name');
    }

    return t('org_selector_no_org');
  };

  // Get organizations to display - ALWAYS prefer userOrganizations if available
  // Only fallback to activeOrganization if userOrganizations is empty/not loaded yet
  const displayOrganizations = (userOrganizations && userOrganizations.length > 0) 
    ? userOrganizations 
    : (activeOrganization ? [activeOrganization] : []);
  
  // Removed excessive logging that was causing performance issues

  const getRoleDisplayName = (roleCode: string): string => {
    const roleMap: Record<string, string> = {
      'super_user': t('org_selector_role_super_user'),
      'admin': t('org_selector_role_admin'),
      'user': t('org_selector_role_user'),
    };
    return roleMap[roleCode] || roleCode;
  };

  const getRoleBadgeClass = (roleCode: string): string => {
    if (roleCode === 'super_user') {
      return 'bg-purple-900 text-purple-300';
    }
    if (roleCode === 'admin') {
      return 'bg-blue-900 text-blue-300';
    }
    return 'bg-green-900 text-green-300';
  };

  console.log(`[OrganizationSelector] Rendering button with org: ${getCurrentOrganizationName()}, displayOrganizations.length=${displayOrganizations.length}`);
  
  return (
    <div className="relative mr-2" data-testid="organization-selector">
      <button
        ref={buttonRef}
        onClick={() => {
          console.log(`[OrganizationSelector] Button clicked, showDropdown=${showDropdown}`);
          setShowDropdown(!showDropdown);
        }}
        className="flex items-center space-x-2 text-white bg-gray-700 px-3 py-2 rounded hover:bg-gray-600 transition"
        title={t('org_selector_change')}
        style={{ display: 'flex', visibility: 'visible' }}
      >
        <span className="text-sm">🏢 {getCurrentOrganizationName()}</span>
        <span className="text-xs">▼</span>
      </button>
      
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 mt-2 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-[9999] w-64"
          style={{ minWidth: '250px' }}
        >
          <div className="p-2 border-b border-gray-700">
            <p className="text-ink-subtle text-sm font-medium">{t('org_selector_select')}</p>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {displayOrganizations.length === 0 ? (
              <div className="p-3 text-ink-subtle text-sm text-center">
                {t('org_selector_loading')}
              </div>
            ) : (
              displayOrganizations.map((org: any) => {
                const isSuspended = org.active === false;
                const isBlocked = isSuspended && !isSuperUser;
                const isActive = activeOrganization?.organization_id === org.organization_id;
                return (
                <button
                  key={org.organization_id}
                  onClick={() => handleOrganizationChange(org)}
                  disabled={isBlocked}
                  title={isBlocked ? 'This organization is suspended' : undefined}
                  className={`w-full text-left p-3 flex items-center transition ${
                    isBlocked
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-700 cursor-pointer'
                  } ${isActive ? 'bg-gray-700 border-l-2 border-blue-500' : ''}`}
                >
                  <div className="flex-1">
                    <div className={`font-medium ${isBlocked ? 'text-ink-subtle' : 'text-white'}`}>
                      {org.organization_name || t('org_selector_no_name')}
                    </div>
                    <div className="text-xs text-ink-subtle flex items-center mt-1 space-x-2 flex-wrap gap-y-1">
                      <span className={`px-2 py-0.5 rounded ${getRoleBadgeClass(org.role_code)}`}>
                        {getRoleDisplayName(org.role_code)}
                      </span>
                      {org.is_primary && (
                        <span className="px-2 py-0.5 rounded bg-yellow-900 text-yellow-300">
                          {t('org_selector_primary')}
                        </span>
                      )}
                      {isSuspended && (
                        <span className="px-2 py-0.5 rounded bg-red-900 text-red-300">
                          Suspended
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && (
                    <span className="text-blue-400 dark:text-blue-500 ml-2">✓</span>
                  )}
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;

